import { useCallback } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";

import { PollingIntervalGroups } from "apollo/constants";
import usePolledQuery from "apollo/usePolledQuery";
import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import NotFoundPage from "components/error/NotFoundPage";
import { EmptystateVcsColored } from "components/icons/generated";
import PageLoading from "components/loading/PageLoading";
import PageWrapper from "components/PageWrapper";
import ReadMoreDocsLink from "components/ReadMoreDocsLink";
import Button from "ds/components/Button";
import EmptyState from "ds/components/EmptyState";
import PageInfoHeader from "ds/components/PageInfoHeader";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { getDocsUrl } from "utils/getDocsUrl";
import { useLayoutActions } from "views/Account/Integrations/hooks/useLayoutActions";
import { useLayoutHeading } from "views/Account/Integrations/hooks/useLayoutHeading";
import { IntegrationsOutletContext } from "views/Account/Integrations/types";
import { AccountContext } from "views/AccountWrapper";

import VcsAgentPoolActions from "../components/VcsAgentPoolActions";
import { showVcsAgentPoolDetailsDrawer } from "../components/VcsAgentPoolDetailsDrawer";
import AgentItem from "./AgentItem";
import { GET_VCS_AGENT_POOL } from "./gql";

const VCSAgentPool = () => {
  const { integrationId: vcsAgentPoolId } = useParams<{ integrationId: string }>();
  const { integration } = useOutletContext<IntegrationsOutletContext>();
  const { accountName } = useTypedContext(AccountContext);

  const navigate = useNavigate();

  const { data, loading, error } = usePolledQuery(GET_VCS_AGENT_POOL, {
    variables: { vcsAgentPoolId },
    pollingGroup: PollingIntervalGroups.SingleEntities,
  });

  useTitle(`${data?.vcsAgentPool?.name || "VCS agent pool"} · ${accountName}`);

  const handleDelete = useCallback(() => {
    navigate(`/integrations/${integration.id}`);
  }, [navigate, integration.id]);

  useBreadcrumbs(
    [
      {
        title: "Integrations",
        link: "/integrations",
      },
      {
        title: integration.name,
        link: `/integrations/${integration.id}`,
      },
      {
        title: data?.vcsAgentPool.name ?? "",
      },
    ],
    [data?.vcsAgentPool.name]
  );
  useLayoutHeading(data?.vcsAgentPool.name ?? "");
  useLayoutActions(
    data?.vcsAgentPool ? (
      <>
        <Button
          variant="secondary"
          onPress={() =>
            showVcsAgentPoolDetailsDrawer({
              id: data.vcsAgentPool.id,
              vcsAgentPool: data.vcsAgentPool,
            })
          }
        >
          Details
        </Button>
        <VcsAgentPoolActions
          vcsAgentPool={data?.vcsAgentPool}
          dotsSize="medium"
          onDeleteCallback={handleDelete}
        />
      </>
    ) : null
  );

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data?.vcsAgentPool) {
    return <PageLoading />;
  }

  if (!data?.vcsAgentPool) {
    return <NotFoundPage />;
  }

  return (
    <>
      <PageInfoHeader title="View VCS agent pool" />
      <PageWrapper>
        {data.vcsAgentPool.agentConnections.length === 0 && (
          <EmptyState
            icon={EmptystateVcsColored}
            title="No belonging agents yet"
            caption={
              <>
                No agents belonging to this VCS Agent Pool are currently connected to our gateway.
                Feel free to refer to the docs on{" "}
                <ReadMoreDocsLink
                  docsUrl={getDocsUrl(
                    /* @ignore-checking-sh-docs */
                    "/concepts/vcs-agent-pools"
                  )}
                  title="how to set up VCS agents"
                />
                .
              </>
            }
          />
        )}
        {data.vcsAgentPool.agentConnections.length > 0 &&
          data.vcsAgentPool.agentConnections.map((item) => <AgentItem key={item.id} {...item} />)}
      </PageWrapper>
    </>
  );
};

export default VCSAgentPool;
